<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';

import KanbanColumn from './KanbanColumn.vue';
import {
  KANBAN_STAGES,
  KANBAN_STAGE_KEYS,
  STATUS_FILTER_OPTIONS,
  getConversationKanbanStage,
} from './constants';

const store = useStore();
const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const allConversations = useMapGetter('getAllConversations');
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');
const agents = useMapGetter('agents/getVerifiedAgents');
const currentUserId = computed(() => currentUser.value?.id || null);

const isBooting = ref(true);
const isRefreshing = ref(false);
const draggedConversationId = ref(null);
const activeDropStage = ref(null);
const persistingConversationIds = ref([]);

const searchQuery = ref('');
const selectedInboxId = ref('all');
const selectedTeamId = ref('all');
const selectedAssigneeId = ref('all');
const selectedStatus = ref('all');

const sortedInboxes = computed(() => {
  return [...inboxes.value].sort((left, right) =>
    left.name.localeCompare(right.name)
  );
});

const sortedTeams = computed(() => {
  return [...teams.value].sort((left, right) =>
    left.name.localeCompare(right.name)
  );
});

const sortedAgents = computed(() => {
  return [...agents.value].sort((left, right) =>
    left.name.localeCompare(right.name)
  );
});

const buildSearchIndex = conversation => {
  return [
    conversation.id,
    conversation.meta?.sender?.name,
    conversation.meta?.sender?.email,
    conversation.meta?.sender?.phone_number,
    conversation.meta?.assignee?.name,
    conversation.meta?.team?.name,
    conversation.last_non_activity_message?.content,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const matchesFilters = conversation => {
  if (conversation.status === wootConstants.STATUS_TYPE.SNOOZED) {
    return false;
  }

  if (
    selectedInboxId.value !== 'all' &&
    conversation.inbox_id !== Number(selectedInboxId.value)
  ) {
    return false;
  }

  if (
    selectedTeamId.value !== 'all' &&
    conversation.meta?.team?.id !== Number(selectedTeamId.value)
  ) {
    return false;
  }

  if (selectedAssigneeId.value !== 'all') {
    const assigneeId = conversation.meta?.assignee?.id;
    if (selectedAssigneeId.value === 'unassigned') {
      if (assigneeId) return false;
    } else if (assigneeId !== Number(selectedAssigneeId.value)) {
      return false;
    }
  }

  if (
    selectedStatus.value !== 'all' &&
    conversation.status !== selectedStatus.value
  ) {
    return false;
  }

  if (!searchQuery.value.trim()) {
    return true;
  }

  return buildSearchIndex(conversation).includes(
    searchQuery.value.toLowerCase()
  );
};

const filteredConversations = computed(() => {
  return [...allConversations.value]
    .filter(matchesFilters)
    .sort((left, right) => right.last_activity_at - left.last_activity_at);
});

const conversationsByStage = computed(() => {
  const grouped = KANBAN_STAGES.reduce((result, stage) => {
    result[stage.id] = [];
    return result;
  }, {});

  filteredConversations.value.forEach(conversation => {
    grouped[getConversationKanbanStage(conversation)].push(conversation);
  });

  return grouped;
});

const boardCount = computed(() => filteredConversations.value.length);

const setPersisting = (conversationId, shouldPersist) => {
  if (shouldPersist) {
    persistingConversationIds.value = [
      ...new Set([...persistingConversationIds.value, conversationId]),
    ];
    return;
  }

  persistingConversationIds.value = persistingConversationIds.value.filter(
    id => id !== conversationId
  );
};

const refreshBoard = async () => {
  isRefreshing.value = true;
  store.dispatch('clearConversationFilters');
  store.dispatch('setActiveInbox', null);
  store.dispatch('emptyAllConversations');

  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      store.dispatch('teams/get'),
      store.dispatch('agents/get'),
    ]);
    await store.dispatch('fetchKanbanConversations');
  } finally {
    isRefreshing.value = false;
    isBooting.value = false;
  }
};

const moveConversationToStage = async (conversation, stage) => {
  if (!conversation) return;
  if (persistingConversationIds.value.includes(conversation.id)) return;
  if (getConversationKanbanStage(conversation) === stage) return;

  const customAttributes = {
    ...(conversation.custom_attributes || {}),
    kanban_stage: stage,
  };

  setPersisting(conversation.id, true);

  try {
    if (stage === KANBAN_STAGE_KEYS.DONE) {
      await store.dispatch('toggleStatus', {
        conversationId: conversation.id,
        status: wootConstants.STATUS_TYPE.RESOLVED,
        customAttributes,
      });
    } else if (conversation.status === wootConstants.STATUS_TYPE.RESOLVED) {
      await store.dispatch('toggleStatus', {
        conversationId: conversation.id,
        status: wootConstants.STATUS_TYPE.OPEN,
        customAttributes,
      });
    } else {
      await store.dispatch('updateCustomAttributes', {
        conversationId: conversation.id,
        customAttributes,
      });
    }
  } catch (error) {
    useAlert(t('KANBAN.ALERTS.MOVE_FAILED'));
  } finally {
    setPersisting(conversation.id, false);
  }
};

const assignToMe = async conversation => {
  const userId = currentUser.value?.id;
  if (!userId) return;

  setPersisting(conversation.id, true);

  try {
    await store.dispatch('assignAgent', {
      conversationId: conversation.id,
      agentId: userId,
    });
  } catch (error) {
    useAlert(t('KANBAN.ALERTS.ASSIGN_FAILED'));
  } finally {
    setPersisting(conversation.id, false);
  }
};

const handleDragStart = conversation => {
  draggedConversationId.value = conversation.id;
};

const handleDragEnd = () => {
  draggedConversationId.value = null;
  activeDropStage.value = null;
};

const handleDrop = async stageId => {
  const conversation = filteredConversations.value.find(
    item => item.id === draggedConversationId.value
  );

  await moveConversationToStage(conversation, stageId);
  handleDragEnd();
};

onMounted(async () => {
  await refreshBoard();
});
</script>

<template>
  <section class="flex h-full min-h-0 w-full flex-col bg-n-surface-1">
    <header
      class="border-b border-n-weak bg-n-alpha-2 px-6 py-5 backdrop-blur-sm"
    >
      <div
        class="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between"
      >
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.24em] text-n-slate-11">
            {{ t('KANBAN.OVERLINE') }}
          </p>
          <h1 class="mt-2 text-2xl font-semibold text-n-slate-12">
            {{ t('KANBAN.TITLE') }}
          </h1>
          <p class="mt-2 max-w-3xl text-sm text-n-slate-11">
            {{ t('KANBAN.DESCRIPTION', { count: boardCount }) }}
          </p>
        </div>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-2xl border border-n-strong px-4 py-2 text-sm font-medium text-n-slate-12 transition hover:bg-n-alpha-black2 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isRefreshing"
          @click="refreshBoard"
        >
          {{
            isRefreshing
              ? t('KANBAN.REFRESHING')
              : t('KANBAN.REFRESH_ACTION')
          }}
        </button>
      </div>

      <div class="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.FILTERS.SEARCH') }}
          <input
            v-model.trim="searchQuery"
            type="text"
            class="rounded-2xl border border-n-strong bg-white px-3 py-2 text-sm text-n-slate-12 shadow-sm outline-none transition focus:border-n-brand dark:bg-n-solid-2"
            :placeholder="t('KANBAN.FILTERS.SEARCH_PLACEHOLDER')"
          />
        </label>

        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.FILTERS.INBOX') }}
          <select
            v-model="selectedInboxId"
            class="rounded-2xl border border-n-strong bg-white px-3 py-2 text-sm text-n-slate-12 shadow-sm outline-none transition focus:border-n-brand dark:bg-n-solid-2 dark:[color-scheme:dark]"
          >
            <option value="all">
              {{ t('KANBAN.FILTERS.ALL_INBOXES') }}
            </option>
            <option
              v-for="inbox in sortedInboxes"
              :key="inbox.id"
              :value="String(inbox.id)"
            >
              {{ inbox.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.FILTERS.TEAM') }}
          <select
            v-model="selectedTeamId"
            class="rounded-2xl border border-n-strong bg-white px-3 py-2 text-sm text-n-slate-12 shadow-sm outline-none transition focus:border-n-brand dark:bg-n-solid-2 dark:[color-scheme:dark]"
          >
            <option value="all">
              {{ t('KANBAN.FILTERS.ALL_TEAMS') }}
            </option>
            <option
              v-for="team in sortedTeams"
              :key="team.id"
              :value="String(team.id)"
            >
              {{ team.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.FILTERS.ASSIGNEE') }}
          <select
            v-model="selectedAssigneeId"
            class="rounded-2xl border border-n-strong bg-white px-3 py-2 text-sm text-n-slate-12 shadow-sm outline-none transition focus:border-n-brand dark:bg-n-solid-2 dark:[color-scheme:dark]"
          >
            <option value="all">
              {{ t('KANBAN.FILTERS.ALL_ASSIGNEES') }}
            </option>
            <option value="unassigned">
              {{ t('KANBAN.CARD.UNASSIGNED') }}
            </option>
            <option
              v-for="agent in sortedAgents"
              :key="agent.id"
              :value="String(agent.id)"
            >
              {{ agent.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.FILTERS.STATUS') }}
          <select
            v-model="selectedStatus"
            class="rounded-2xl border border-n-strong bg-white px-3 py-2 text-sm text-n-slate-12 shadow-sm outline-none transition focus:border-n-brand dark:bg-n-solid-2 dark:[color-scheme:dark]"
          >
            <option
              v-for="option in STATUS_FILTER_OPTIONS"
              :key="option.id"
              :value="option.id"
            >
              {{ t(option.titleKey) }}
            </option>
          </select>
        </label>
      </div>
    </header>

    <div class="min-h-0 flex-1 overflow-auto px-4 py-4 md:px-6">
      <div
        v-if="isBooting"
        class="flex h-full min-h-[18rem] items-center justify-center rounded-3xl border border-dashed border-n-strong text-sm text-n-slate-11"
      >
        {{ t('KANBAN.LOADING') }}
      </div>

      <div
        v-else-if="!boardCount"
        class="flex h-full min-h-[18rem] flex-col items-center justify-center rounded-3xl border border-dashed border-n-strong px-6 text-center"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('KANBAN.EMPTY_STATE.TITLE') }}
        </h2>
        <p class="mt-2 max-w-md text-sm text-n-slate-11">
          {{ t('KANBAN.EMPTY_STATE.DESCRIPTION') }}
        </p>
      </div>

      <div v-else class="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <KanbanColumn
          v-for="stage in KANBAN_STAGES"
          :key="stage.id"
          :stage="stage"
          :conversations="conversationsByStage[stage.id]"
          :is-drop-target="activeDropStage === stage.id"
          :dragged-conversation-id="draggedConversationId"
          :persisting-conversation-ids="persistingConversationIds"
          :current-user-id="currentUserId"
          @assign-to-me="assignToMe"
          @drag-start="handleDragStart"
          @drag-end="handleDragEnd"
          @drag-enter="activeDropStage = $event"
          @drag-leave="
            activeDropStage = activeDropStage === $event ? null : activeDropStage
          "
          @drop="handleDrop"
        />
      </div>
    </div>
  </section>
</template>
